import { EventEmitter, Injectable } from '@angular/core';
import { FilesService } from '../api/files.service';
import { TranslocoService } from '@jsverse/transloco';
import { RecentUploadsService } from './recent-uploads.service';
import { UserDataService } from './user-data.service';
import { SendNotificationService } from './send-notification.service';
import { ToastComponent } from '../../shared/components/toast/toast.component';
import { FilesInExtractionResponse, UploadedFile } from '../interfaces/file-upload-interfaces';
import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { catchError, concatMap, finalize, forkJoin, from, mapTo, mergeMap, Observable, of, Subject, takeUntil, tap, timer } from 'rxjs';
import { CommodityType } from '../interfaces/invoice-interfaces';

type UploadTask = {
  file: File;
  recentUploadsIndex: number;
  cancel$: Subject<void>;
};

@Injectable({
  providedIn: 'root'
})
export class FileUploadHelperService {
  constructor(private filesService: FilesService, private translocoService: TranslocoService, private recentUploads: RecentUploadsService, private userData: UserDataService,
    private sendNotificationService: SendNotificationService
  ) { }

  // Batch size restrictions to avoid rate limits
  readonly batchSize = 3;
  readonly interBatchDelayMs = 5000;

  files: File[] = []; // Files dragged into input

  successCount: number = 0;
  failCount: number = 0;

  // Save toast id to avoid spamming user in batch upload
  successToastId: number = -1;
  failToastId: number = -1;
  noActiveMembershipToastId: number = -1;

  // Failed notification
  notificationSent: boolean = false;

  // Failed file names
  failedFileNames: string[] = [];

  // Toast
  toast?: ToastComponent;

  attachToast(toast: ToastComponent) {
    this.toast = toast;
  }

  // Upload Completed
  uploadCompleted?: EventEmitter<{ success: boolean }>;

  attachUploadCompleted(uploadCompleted?: EventEmitter<{ success: boolean }>) {
    this.uploadCompleted = uploadCompleted;
  }

  allUploadsCompletedSubject = new Subject<{ success: boolean }>();
  allUploadsCompleted$ = this.allUploadsCompletedSubject.asObservable();

  onFileSelected(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target.files) {
      this.handleFiles(target.files);
      target.value = '';
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer?.files) {
      this.handleFiles(event.dataTransfer.files);
    }
  }

  filterAllowedFiles(fileList: FileList | File[]): File[] {
    const allowedTypes = [
      'application/pdf',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    return Array.from(fileList).filter(file =>
      allowedTypes.includes(file.type) ||
      file.name.toLowerCase().endsWith('.pdf') ||
      file.name.toLowerCase().endsWith('.xls') ||
      file.name.toLowerCase().endsWith('.xlsx')
    );
  }

  handleFiles(fileList: FileList) {
    this.files = this.filterAllowedFiles(fileList);

    if (this.files.length === 0) {
      if (this.toast) {
        this.toast.showToast("shared.file_upload.invalid_type", "shared.file_upload.please_upload_valid_type", "error", 4000, true);
      }
      return;
    }

    // Reset failed notification data
    this.notificationSent = false;
    this.failedFileNames = [];

    this.uploadFiles();
  }

  async handleSuccessfulFileUpload(uploadIndex: number) {
    // Track success for message
    this.successCount++;
    this.showSuccessMessage();

    this.recentUploads.getUploadByIndex(uploadIndex).currentUploadProgress = 100;

    // Emit upload completed
    if (this.uploadCompleted != undefined) {
      this.uploadCompleted.emit({ success: true });
    }

    // Reload user data
    await this.userData.loadUserData(false);
  }

  async uploadFiles() {
    if ((this.recentUploads.cancelAll$ as any).isStopped) this.recentUploads.cancelAll$ = new Subject<void>();

    const tasks: UploadTask[] = this.files.map((file) => {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      const fileIconSrc: string = this.filesService.getFileIconSrcByFileType(fileExtension);

      const uploadedFile: UploadedFile = {
        fileSrc: fileIconSrc,
        fileName: file.name,
        fileSizeMB: Number((file.size / (1024 * 1024)).toFixed(2)) + ' MB',
        currentUploadProgress: 0,
        paused: false,
        uploadSubscription: null,
        canceled: false
      };

      const recentUploadsIndex = this.recentUploads.addUpload(uploadedFile);
      return { file, recentUploadsIndex, cancel$: new Subject<void>() };
    });

    const batches = this.chunkArray(tasks, this.batchSize);

    this.recentUploads.batchPipelineSub = from(batches).pipe(
      takeUntil(this.recentUploads.cancelAll$),
      concatMap(batch => {
        const uploads$ = batch.map(task => this.startSingleUpload$(task));
        return forkJoin(uploads$).pipe(
          // wait before starting the next batch
          concatMap(() => timer(this.interBatchDelayMs).pipe(mapTo(void 0)))
        );
      })
    ).subscribe({
      complete: () => {
        this.checkTotalFailed(); 
      }
    });
  }

  chunkArray<T>(arr: T[], size: number): T[][] {
    const out: T[][] = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  }

  startSingleUpload$(task: UploadTask): Observable<void> {
    const { file, recentUploadsIndex, cancel$ } = task;

    const record = this.recentUploads.getUploadByIndex(recentUploadsIndex);
    if (!record || record.canceled || record.currentUploadProgress === 100) {
      return of(void 0);
    }

    const req$ = this.filesService.upload(file).pipe(
      takeUntil(cancel$),
      takeUntil(this.recentUploads.cancelAll$),
      tap(event => {
        if (event.type === HttpEventType.UploadProgress) {
          this.recentUploads.getUploadByIndex(recentUploadsIndex).currentUploadProgress = 0;
        } else if (event.type === HttpEventType.Response) {
          // Success
          this.handleSuccessfulFileUpload(recentUploadsIndex);
        }
      }),
      catchError(async (err) => {
        let errorShown = false;
        let crid = 'UNKNOWN-CRID';
        let successful = false;

        if (err instanceof HttpErrorResponse) {
          crid = err.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
          if (err.error?.code === 'SVC-5060' || err.error?.code === 'SVC-5061' || err.error?.code === 'SVC-4062') {
            await this.handleSuccessfulFileUpload(recentUploadsIndex);
            successful = true;
            errorShown = true;
          } else if (err.error?.code === 'SVC-4303') {
            this.showNoMembershipMessage();
            errorShown = true;
          }
        }

        if (!successful) {
          this.recentUploads.setCanceled(recentUploadsIndex, true);
          if (file.name) this.failedFileNames.push(file.name);
          this.failCount++;
        }

        if (this.uploadCompleted) {
          this.uploadCompleted.emit({ success: successful });
        }

        if (!errorShown) {
          this.showFailMessage(crid);
        }

        return;
      }),
      finalize(() => {
      })
    );

    const sub = req$.subscribe({
    });
    this.recentUploads.getUploadByIndex(recentUploadsIndex).uploadSubscription = sub;

    return of(void 0);
  }

  showSuccessMessage() {
    if (this.toast == undefined) { return; }

    const uploaded = this.successCount;
    const total = this.files.length;

    const title = this.translocoService.translate("shared.file_upload.successful");
    const message = this.translocoService.translate("shared.file_upload.files_uploaded_progress", { uploaded: uploaded, total: total });

    if (this.successToastId == -1) {
      this.successToastId = this.toast.showToast(title, message, "success", 4000, false);
      return;
    }

    const result = this.toast.updateToast(this.successToastId, title, message, "success", false)

    if (!result) {
      this.successToastId = -1;
      this.showSuccessMessage();
    }

    // Reset numbers if needed
    this.checkTotalFailed()
  }

  showFailMessage(crid: string = '') {
    const failed = this.failCount;
    const total = this.files.length;

    const title = this.translocoService.translate("shared.file_upload.failed");
    const message = this.translocoService.translate("shared.file_upload.files_upload_failed_progress", { failed: failed, total: total, crid: crid });

    if (this.failToastId == -1) {
      if (this.toast) {
        this.failToastId = this.toast.showToast(title, message, "error", 8000, false);
      }
      this.checkFailNotificationSend();
      return;
    }

    if (this.toast) {
      const result = this.toast.updateToast(this.failToastId, title, message, "error", false)

      if (!result) {
        this.failToastId = -1;
        this.showFailMessage();
      }
    }

    // Reset numbers if needed
    this.checkFailNotificationSend();
    this.checkTotalFailed()
  }

  checkTotalFailed() {
    if (this.failCount + this.successCount >= this.files.length) {
      const atLeastOneSuccess = this.successCount > 0;

      this.allUploadsCompletedSubject.next({ success: atLeastOneSuccess });
      this.successCount = this.failCount = 0;
    }
  }

  checkFailNotificationSend() {
    if (this.failCount + this.successCount >= this.files.length && !this.notificationSent) {
      const filesStr = this.failedFileNames.join(', ');
      this.sendNotificationService.sendFailedUploadNotification(this.failCount, filesStr)

      this.notificationSent = true;
    }
  }

  showNoMembershipMessage() {
    if (this.toast == undefined) { return; }

    const title = this.translocoService.translate("shared.membership_error");
    const message = this.translocoService.translate("shared.no_active_membership");

    if (this.noActiveMembershipToastId == -1) {
      this.noActiveMembershipToastId = this.toast.showToast(title, message, 'error', 6000, true);
      return;
    }

    // Repeat if toast doesn't exist anymore
    const result = this.toast.updateToast(this.noActiveMembershipToastId, title, message, "error", true)
    if (!result) {
      this.noActiveMembershipToastId = -1;
      this.showNoMembershipMessage();
    }
  }

  getTotalUploadingNumber(): number {
    return this.files.length || 10;
  }
}
